import createCache from "@emotion/cache";
import { CacheProvider, ThemeProvider } from "@emotion/react";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, Grid, Modal, Typography } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { Formik } from "formik";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { object, string } from "yup";
import AutocomplateCheckbox from "../../../components/dropdown/AutocomplateCheckbox";
import MaterialTextFieldComponent from "../../../components/textField/MaterialTextFieldCmp";
import { GetCancellationReasonsResponse } from "../../../models/loan.model";
import { CancelLoan } from "../../../services/loan.service";
import { v4 as uuid } from "uuid";
import { useHistory } from "react-router-dom";

const styles = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  minWidth: {
    xs: 250,
    sm: 300,
    md: 400,
    lg: 500,
  },
  maxWidth: {
    xs: 250,
    sm: 300,
    md: 400,
    lg: 500,
  },

  maxHeight: "70vh",
  minHeight: "30vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  // overflow: "auto",
  // overflowX: "hidden",
  paddingRight: "3vh",
  paddingTop: "4vh",
  // direction: "ltr",
  borderRadius: "5px",
  "&::-webkit-scrollbar": {
    width: 7,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#F9FAFC",
    border: "2px solid #DAE0E4",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#9AAFC4",
  },
};

const theme = createTheme({
  direction: "rtl", // Both here and <body dir="rtl">
});
// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

interface Props {
  openModal: boolean;
  setOpenModal: (openModal: boolean) => void;
  cancellationReasonsList: GetCancellationReasonsResponse[];
  referenceKey: string;
  setReload: (reload: string) => void;
}

const LoanCancelationModal = ({
  openModal,
  setOpenModal,
  cancellationReasonsList,
  referenceKey,
  setReload,
}: Props) => {
  const [cancleReasonsValue, setCancleReasonsValue] = useState<any[]>([]);
  const [cancleReasonIds, setCancleReasonIds] = useState<number[]>([]);
  const history = useHistory();

  const initialValues: { description: string } = {
    description: "",
  };
  const handleCancle = (value: { description: string }) => {
    CancelLoanMutate({
      referenceKey,
      cancellationReasons: cancleReasonIds,
      description: value.description,
    });
  };

  const changeCancleReasons = (e: any, value: any) => {
    setCancleReasonIds(e);
    setCancleReasonsValue(value);
  };

  const removeTag = (id: number) => {
    setCancleReasonIds(cancleReasonIds.filter((reasonId) => reasonId !== id));
    setCancleReasonsValue(
      cancleReasonsValue.filter((reason) => reason.id !== id)
    );
  };

  const { mutate: CancelLoanMutate } = useMutation(CancelLoan, {
    onSuccess: (data: any) => {
      if (data) {
        setOpenModal(false);
        setCancleReasonIds([]);
        setCancleReasonsValue([]);
        toast.success("تسهیلات با موفقیت لغو گردید");
        history.push({
          pathname: "/CancleLoanList",
        });
      }
    },
    onError: () => {},
  });
  const validation = object().shape({
    description: string()
      .ensure()
      .max(500, "توضیحات نباید بیش از 500 کاراکتر باشد.")
      .when([], {
        is: () => cancleReasonIds.length === 0,
        then: string().required("توضیحات الزامی است."),
      }),
  });
  return (
    <div>
      <Modal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setCancleReasonsValue([]);
          setCancleReasonIds([]);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styles}>
          <Grid
            container
            flexDirection={"column"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={"2.5rem"}
          >
            <Grid item>
              <Typography
                component={"p"}
                display={"block"}
                textAlign={"center"}
                fontWeight={"bold"}
                // marginY={"3rem"}
                fontSize={"14px"}
                color={"#1D4A87"}
              >
                لغو تسهیلات
              </Typography>
            </Grid>

            {/* formik */}
            <Formik
              initialValues={initialValues}
              onSubmit={handleCancle}
              validateOnMount={true}
              validationSchema={validation}
            >
              {({
                handleSubmit,
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
              }) => (
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                >
                  <Grid
                    container
                    xs={12}
                    spacing={2}
                    style={{ paddingBottom: "20px" }}
                  >
                    <Grid
                      item
                      width={"100%"}
                      sx={{ paddingLeft: "unset !important" }}
                      className="dropdownCmp2-container"
                    >
                      <CacheProvider value={cacheRtl}>
                        <ThemeProvider theme={theme}>
                          <div dir="rtl">
                            <AutocomplateCheckbox
                              id="cancellationReasons"
                              name="cancellationReasons"
                              value={cancleReasonIds}
                              label="علل لغو تسهیلات"
                              options={cancellationReasonsList}
                              onChange={changeCancleReasons}
                              changeMultiSelect={changeCancleReasons}
                              responseValue={cancleReasonsValue}
                            />
                          </div>
                        </ThemeProvider>
                      </CacheProvider>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        paddingLeft: "0px !important",
                        marginBottom: "30px",
                        height: "14vh",
                      }}
                    >
                      <MaterialTextFieldComponent
                        placeHolder={"توضیحات"}
                        label={"توضیحات"}
                        name={"description"}
                        id={"description"}
                        value={values.description}
                        onChange={handleChange}
                        required={cancleReasonIds.length === 0}
                        helperText={
                          touched.description ? errors.description : ""
                        }
                        error={
                          touched.description && Boolean(errors.description)
                        }
                        onBlur={handleBlur}
                        multiline
                        sx={{
                          "& .MuiOutlinedInput-root > .MuiInputBase-input ": {
                            fontSize: 13,
                            lineHeight: "2vh !important",
                            height: "6vh !important",
                          },

                          "& .MuiInputBase-root": {
                            height: "14vh !important",
                          },
                        }}
                      />
                    </Grid>

                    {cancleReasonsValue.length > 0 && (
                      <Box
                        sx={{
                          borderTop: "1px solid #CCD1D6",
                          width: "100%",
                          paddingTop: "1rem",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#1D4A87",
                            marginTop: "10px",
                            fontWeight: "bold",
                            marginBottom: "10px",
                          }}
                        >
                          دلایل اضافه شده
                        </Typography>
                        <Grid
                          item
                          sx={{ display: "flex", gap: "7px", flexWrap: "wrap" }}
                        >
                          {cancleReasonsValue.map((node) => (
                            <Box
                              sx={{
                                background: "#EDEFEF",
                                padding: "3px 7px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderRadius: "5px",
                                whiteSpace: "nowrap",
                                marginBottom: "20px",
                              }}
                            >
                              {node.title}
                              <Typography
                                sx={{ cursor: "pointer" }}
                                onClick={() => removeTag(node.id)}
                              >
                                <ClearIcon
                                  sx={{ color: "#34495E", fontSize: "14px" }}
                                />
                              </Typography>
                            </Box>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    <Grid
                      container
                      justifyContent={"center"}
                      alignItems={"center"}
                      marginBottom={"8px"}
                      marginTop={"20px"}
                    >
                      <Grid
                        item
                        xs={6}
                        width={"100%"}
                        sx={{ paddingInline: "10px" }}
                      >
                        <Button
                          fullWidth
                          type="submit"
                          variant="contained"
                          disabled={
                            !values.description && cancleReasonIds.length === 0
                          }
                          className={
                            !values.description && cancleReasonIds.length === 0
                              ? "btnForm DangerDisable"
                              : "btnForm DangerActive"
                          }
                        >
                          لغو تسهیلات
                        </Button>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        width={"100%"}
                        sx={{ paddingInline: "10px" }}
                      >
                        <Button
                          onClick={() => {
                            setOpenModal(false);
                            setCancleReasonIds([]);
                            setCancleReasonsValue([]);
                          }}
                          fullWidth
                          variant="contained"
                          style={{
                            fontSize: "1rem",
                            color: "#fff",
                            height: "40px",
                            background: "#607D8B",
                          }}
                        >
                          بازگشت
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};

export default LoanCancelationModal;
